import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, User, Building2, Shield, Pencil, LogOut, Trash2, AlertTriangle, ChevronRight, Lock } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../auth/AuthContext";
import { Card, PageHeader, Button } from "../components/Primitives";

/** Read-only info row (label · value). Reuses existing visual style. */
function InfoRow({ icon: Icon, label, value, mono = false, testid }) {
  return (
    <div className="p-4 flex items-center gap-3" data-testid={testid}>
      <div className="w-9 h-9 rounded-lg bg-carbon-800 border border-carbon-600 flex items-center justify-center text-ink-500 shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-mono uppercase tracking-wider text-ink-500">{label}</div>
        <div className={`text-sm text-ink-900 truncate ${mono ? "font-mono" : ""}`} data-testid={`${testid}-value`}>
          {value || "—"}
        </div>
      </div>
    </div>
  );
}

/** Navigation row (label · caret). */
function NavRow({ icon: Icon, label, hint, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="w-full p-4 flex items-center gap-3 hover:bg-carbon-800/40 transition text-left">
      <div className="w-9 h-9 rounded-lg bg-carbon-800 border border-carbon-600 flex items-center justify-center text-ink-500 shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-ink-900">{label}</div>
        {hint && <div className="text-[11px] text-ink-500 mt-0.5">{hint}</div>}
      </div>
      <ChevronRight className="w-4 h-4 text-ink-500 shrink-0" />
    </button>
  );
}

export default function Account() {
  const { t, lang } = useI18n();
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();
  const [showDelete, setShowDelete] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const accountType = profile?.account_type === "ORGANIZATION"
    ? t("account.typeOrg")
    : t("account.typePerson");
  const isOrg = profile?.account_type === "ORGANIZATION";

  const doSignOut = async () => {
    try { await signOut(); navigate("/", { replace: true }); }
    catch (e) { toast.error(e?.message || "Sign out failed"); }
  };

  // Server-side deletion is intentionally not wired in V1 (no service-role endpoint yet).
  // The UI is present and confirmation works, but the destructive action shows a
  // release-safe explanation instead of executing an unsafe client-side delete.
  const requestDelete = () => {
    toast.error(t("account.deleteNotAvailable"));
  };

  return (
    <div className="max-w-2xl mx-auto p-4 md:p-8 animate-fade-up" data-testid="account-page">
      <PageHeader title={t("account.title")} sub={t("account.subtitle")} />

      {/* Email */}
      <section className="mb-6">
        <div className="text-[10px] font-mono uppercase tracking-wider text-ink-500 mb-2 px-1">
          {t("account.credentialsTitle")}
        </div>
        <Card className="divide-y divide-carbon-600" data-testid="account-credentials">
          <InfoRow icon={Mail} label={t("account.emailLabel")} value={user?.email} mono testid="account-email" />
          <div className="px-4 py-3 text-[11px] text-ink-500 flex items-start gap-2">
            <Lock className="w-3 h-3 mt-0.5 shrink-0" />
            <span>{t("account.emailHint")}</span>
          </div>
        </Card>
      </section>

      {/* Account type */}
      <section className="mb-6">
        <div className="text-[10px] font-mono uppercase tracking-wider text-ink-500 mb-2 px-1">
          {t("account.typeTitle")}
        </div>
        <Card data-testid="account-type-card">
          <InfoRow
            icon={isOrg ? Building2 : User}
            label={t("account.typeLabel")}
            value={accountType}
            testid="account-type"
          />
        </Card>
        <p className="text-[11px] text-ink-500 mt-2 px-1">{t("account.typeHint")}</p>
      </section>

      {/* Shortcuts */}
      <section className="mb-6">
        <div className="text-[10px] font-mono uppercase tracking-wider text-ink-500 mb-2 px-1">
          {t("account.manageTitle")}
        </div>
        <Card className="divide-y divide-carbon-600">
          <NavRow
            icon={Pencil}
            label={t("account.editProfile")}
            hint={t("account.editProfileHint")}
            onClick={() => navigate("/app/profile")}
            testid="account-edit-profile"
          />
          <NavRow
            icon={Shield}
            label={t("account.security")}
            hint={t("account.securityHint")}
            onClick={() => navigate("/app/security")}
            testid="account-security"
          />
        </Card>
      </section>

      {/* Sign out */}
      <section className="mb-8">
        <button onClick={doSignOut} data-testid="account-signout"
          className="w-full flex items-center justify-center gap-2 p-4 rounded-xl border border-carbon-600 text-ink-600 hover:text-ink-900 hover:border-electric-500/40 transition">
          <LogOut className="w-4 h-4" />
          {t("account.signOut")}
        </button>
      </section>

      {/* Danger zone — Delete account (V1: UI + confirm, execution not wired) */}
      <section className="mb-16">
        <div className="text-[10px] font-mono uppercase tracking-wider text-red-500/80 mb-2 px-1 flex items-center gap-1.5">
          <AlertTriangle className="w-3 h-3" /> {t("account.dangerTitle")}
        </div>
        <Card className="p-4 border-red-500/30" data-testid="account-danger-zone">
          {!showDelete ? (
            <button onClick={() => setShowDelete(true)} data-testid="account-delete-open"
              className="w-full flex items-center gap-3 text-left">
              <div className="w-9 h-9 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center justify-center text-red-500 shrink-0">
                <Trash2 className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-ink-900">{t("account.deleteTitle")}</div>
                <div className="text-[11px] text-ink-500 mt-0.5">{t("account.deleteHint")}</div>
              </div>
              <ChevronRight className="w-4 h-4 text-ink-500 shrink-0" />
            </button>
          ) : (
            <div className="space-y-3" data-testid="account-delete-confirm">
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/5 border border-red-500/20">
                <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                <div className="text-[11px] text-ink-700 leading-relaxed">
                  {t("account.deletePermanent")}
                </div>
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase tracking-wider text-ink-500 block mb-1.5">
                  {t("account.deleteConfirmLabel")}
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  data-testid="account-delete-input"
                  placeholder={t("account.deleteConfirmWord")}
                  className="w-full bg-carbon-800 border border-carbon-600 rounded-lg px-3.5 py-2.5 text-sm text-ink-900 placeholder:text-ink-500 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500/40 transition"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => { setShowDelete(false); setConfirmText(""); }}
                  data-testid="account-delete-cancel"
                  className="flex-1 !bg-carbon-800 !border-carbon-600 !text-ink-700 hover:!text-ink-900">
                  {t("account.deleteCancel")}
                </Button>
                <Button
                  onClick={requestDelete}
                  disabled={confirmText.trim().toUpperCase() !== t("account.deleteConfirmWord").toUpperCase()}
                  data-testid="account-delete-confirm-btn"
                  className="flex-1 !bg-red-500 hover:!bg-red-600 !border-red-500 !text-white">
                  <Trash2 className="w-4 h-4" />
                  {t("account.deleteConfirmBtn")}
                </Button>
              </div>
            </div>
          )}
        </Card>
      </section>
    </div>
  );
}
